import { serverTimestamp, type Timestamp } from "firebase/firestore";
import { action, computed, makeObservable, reaction } from "mobx";
import type ActivityModel from "../activities/ActivityModel";
import type { Api } from "../core/apis";
import { BaseModel, type BaseModelData } from "../core/BaseModel";
import type { Stores } from "../core/stores";
import { trackEvent } from "../core/trackEvent";
import { log } from "../ui/log";

export type UserProfileData = {
  description: string | null;
  profileQuestions: { [key: string]: string }; // to answer on questions from business
};

export interface UserModelData extends BaseModelData {
  planUid: string;
  businessPlanUid: string;
  authedUsers: string[];
  lastActivity: Timestamp;
  displayName: string;
  email: string | null;
  avatarUrl: string | null;
  moduleVisits: { [key: string]: Timestamp };
  profile: UserProfileData | null;

  // both web + native
  tokens: string[];
}

export default class UserModel extends BaseModel<UserModelData> {
  constructor(stores: Stores, api: Api, data: UserModelData) {
    super(stores, api, data);

    Object.assign(this.data, {
      displayName: data.displayName.trim(),
      moduleVisits: data.moduleVisits ?? {},
      profile: data.profile ?? null,
    });

    // If current super user is verified, and uses this user,
    // email needs to be added + authedUsers synced to that super user
    reaction(
      () => this.stores.sessionStore.isRegistered,
      async (usingRegistredUser) => {
        if (usingRegistredUser) {
          const authUser = this.stores.sessionStore.authUser;

          if (
            authUser &&
            !!authUser.email &&
            this.data.authedUsers.includes(authUser.uid) &&
            !this.data.email
          ) {
            this.data.email = authUser.email;
            this.data.authedUsers = [authUser.uid];
            await this.api.usersApi.set(this);
            console.info("migrated user");
            trackEvent("user_migrated");
          }
        }
      },
      { fireImmediately: true },
    );

    makeObservable(this);
  }

  @computed get isUsed() {
    return this.data.authedUsers.length > 0;
  }

  @action async updateTimestampAndSave() {
    this.data.lastActivity = serverTimestamp() as Timestamp;
    await this.api.usersApi.update(this.data.uid, {
      lastActivity: this.data.lastActivity,
    });
  }

  @action async updateEmail(email: string) {
    if (email && email !== this.data.email) {
      this.data.email = email;
      this.api.usersApi.update(this.uid, { email: this.data.email });
    }
  }

  @action async updateProfileData(info: Partial<UserProfileData>) {
    const current = this.data.profile ?? {
      description: "",
      profileQuestions: {},
    };

    this.data.profile = {
      description: info.description ?? current.description,
      profileQuestions: {
        ...current.profileQuestions,
        ...info.profileQuestions,
      },
    };

    if (this.businessPlan) {
      this.businessPlan.updateUserInfo(this.uid, {
        profile: this.data.profile,
      });
    }

    await this.api.usersApi.update(this.uid, { profile: this.data.profile });
  }

  @action async saveProfileQuestionAnswer(id: string, answer: string) {
    if (!this.data.profile) {
      this.data.profile = {
        description: "",
        profileQuestions: {},
      };
    }

    if (!answer) {
      delete this.data.profile.profileQuestions[id];
    } else {
      this.data.profile.profileQuestions[id] = answer;
    }

    if (this.businessPlan) {
      this.businessPlan.updateUserInfo(this.uid, {
        profile: this.data.profile,
      });
    }

    await this.api.usersApi.update(this.uid, { profile: this.data.profile });
  }

  @action async setDisplayName(name: string) {
    if (name && name !== this.data.displayName) {
      this.data.displayName = name;
      this.api.usersApi.update(this.uid, {
        displayName: this.data.displayName,
      });

      if (this.businessPlan) {
        this.businessPlan.updateUserInfo(this.uid, {
          displayName: this.data.displayName,
        });
      }
    }
  }

  @action async setPlanUid(uid: string) {
    this.data.planUid = uid;

    await this.api.usersApi.update(this.uid, { planUid: this.data.planUid });
  }

  @action async setBusinessPlanUid(uid: string) {
    this.data.businessPlanUid = uid;
    await this.api.usersApi.update(this.uid, {
      businessPlanUid: this.data.businessPlanUid,
    });
  }

  @action async updateTokens(token: string) {
    // for now limit to one token / user
    // to avoid duplicate notifications
    if (this.data.tokens.length > 1) {
      this.data.tokens = [this.data.tokens[0]];
    }

    if (!this.data.tokens.includes(token)) {
      this.data.tokens.push(token);
      await this.api.usersApi.update(this.data.uid, {
        tokens: this.data.tokens,
      });
      log.dev("Tokens updated", this.data.tokens);
    }
    log.dev("Tokens skipped - no new");
  }

  @action async updateVisitedTimestamp(moduleUid: string) {
    log.dev("Updated module timestamp visit");
    this.data.moduleVisits[moduleUid] = serverTimestamp() as Timestamp;
    this.data.lastActivity = serverTimestamp() as Timestamp;
    await this.api.usersApi.update(this.uid, {
      moduleVisits: this.data.moduleVisits,
      lastActivity: this.data.lastActivity,
    });
  }

  @computed get isRegistered() {
    return !!this.data.email;
  }

  @computed get plan() {
    return this.stores.plansStore.byId.get(this.data.planUid);
  }

  @computed get businessPlan() {
    return this.stores.businessPlansStore.byId.get(this.data.businessPlanUid);
  }

  @computed get activityEvents(): ActivityModel[] {
    return this.stores.activitiesStore.all.filter(
      (event) => event.data.userUid === this.data.uid,
    );
  }

  @action addSessionUser() {
    const userUid = this.stores.sessionStore.uid;

    if (userUid) {
      // Registred user
      if (this.data.email && !this.data.authedUsers.includes(userUid)) {
        throw Error("Need to sign in to be this user");
      }

      this.data.authedUsers.push(userUid);

      return this.api.usersApi.set(this);
    }
  }

  @action setAvatarUrl(url: string) {
    this.data.avatarUrl = url;

    if (this.businessPlan) {
      this.businessPlan.updateUserInfo(this.uid, {
        avatarUrl: this.data.avatarUrl,
      });
    }

    return this.api.usersApi.update(this.uid, {
      avatarUrl: this.data.avatarUrl,
    });
  }

  toFireStore(): Omit<UserModelData, "uid"> {
    return {
      ...this.fromBaseModel(),
      ...this.data,
    };
  }
}
